import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Badge, Button, Card, Col, Container, Form, ListGroup, Modal, Row, Spinner, Table } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import Swal from 'sweetalert2'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { CERT_STATUS, CERT_TYPES, beYearOptions, currentBeYear, fmtDateTH } from './aoShared'

const emptyForm = { ct_type: 'student_status', ct_purpose: '', ct_note: '' }

// ธุรการ: ทะเบียนหนังสือรับรอง — คีย์คำขอแทนนักเรียน/บุคลากร
export default function CertificateRegistry() {
  const [year, setYear] = useState(String(currentBeYear()))
  const [type, setType] = useState('')
  const [status, setStatus] = useState('')
  const [q, setQ] = useState('')
  const [certs, setCerts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)
  const [modalError, setModalError] = useState('')

  // ผู้ขอ: นักเรียน (search) หรือครู (dropdown)
  const [studentQuery, setStudentQuery] = useState('')
  const [studentResults, setStudentResults] = useState([])
  const [selectedStudent, setSelectedStudent] = useState(null)
  const [teachers, setTeachers] = useState([])
  const [selectedTeacher, setSelectedTeacher] = useState('')

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const params = new URLSearchParams()
      if (year) params.set('year', year)
      if (type) params.set('type', type)
      if (status) params.set('status', status)
      if (q) params.set('q', q)
      const res = await axiosInstance.get(`/api/ao/certificates?${params.toString()}`)
      if (res.data.success) setCerts(res.data.data)
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [year, type, status, q])

  useEffect(() => { fetchData() }, [fetchData])

  // ค้นหานักเรียนแบบ debounce
  useEffect(() => {
    if (!showModal || studentQuery.trim().length < 2) {
      setStudentResults([])
      return
    }
    const timer = setTimeout(async () => {
      try {
        const res = await axiosInstance.get(`/api/ao/search-students?q=${encodeURIComponent(studentQuery.trim())}`)
        if (res.data.success) setStudentResults(res.data.data)
      } catch { /* ignore */ }
    }, 350)
    return () => clearTimeout(timer)
  }, [showModal, studentQuery])

  const openModal = async () => {
    setForm(emptyForm)
    setSelectedStudent(null)
    setStudentQuery('')
    setStudentResults([])
    setSelectedTeacher('')
    setModalError('')
    setShowModal(true)
    if (!teachers.length) {
      try {
        const res = await axiosInstance.get('/api/ao/teachers')
        if (res.data.success) setTeachers(res.data.data)
      } catch { /* ignore */ }
    }
  }

  const isStudentCert = form.ct_type !== 'staff_employment'

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (isStudentCert && !selectedStudent) {
      setModalError('กรุณาค้นหาและเลือกนักเรียน')
      return
    }
    if (!isStudentCert && !selectedTeacher) {
      setModalError('กรุณาเลือกบุคลากร')
      return
    }
    setSaving(true)
    setModalError('')
    try {
      const res = await axiosInstance.post('/api/ao/certificates', {
        ...form,
        tb_student_st_id: isStudentCert ? selectedStudent.st_id : null,
        tb_teacher_t_id: isStudentCert ? null : selectedTeacher,
      })
      if (res.data.success) {
        setShowModal(false)
        Swal.fire({ icon: 'success', title: `รับคำขอ เลขที่ ${res.data.data.ct_full_no}`, timer: 2000, showConfirmButton: false })
        fetchData()
      }
    } catch (err) {
      setModalError(err.response?.data?.message || 'บันทึกไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }

  const nextStatus = { requested: ['issued', 'ออกเอกสาร'], issued: ['picked_up', 'รับเอกสารแล้ว'] }

  const handleStatus = async (c) => {
    const [status2, label] = nextStatus[c.ct_status] || []
    if (!status2) return
    const result = await Swal.fire({
      icon: 'question',
      title: `${label}?`,
      text: `${c.ct_full_no} — ${c.st_name || c.t_name}`,
      showCancelButton: true,
      confirmButtonText: label,
      cancelButtonText: 'ปิด',
    })
    if (!result.isConfirmed) return
    try {
      await axiosInstance.put(`/api/ao/certificates/${c.ct_id}/status`, { status: status2 })
      fetchData()
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'ดำเนินการไม่สำเร็จ', text: err.response?.data?.message || '' })
    }
  }

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
            <h4 className="main-title mb-0">
              <i className="ri-award-line me-2 text-primary" />
              ทะเบียนหนังสือรับรอง
            </h4>
            <Button variant="primary" size="sm" onClick={openModal}>
              <i className="ri-add-line me-1" />รับคำขอ (ออกเลขทันที)
            </Button>
          </div>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

          <Card className="card-one">
            <Card.Header className="d-flex align-items-center gap-2 flex-wrap">
              <Form.Select size="sm" style={{ width: 120 }} value={year} onChange={e => setYear(e.target.value)}>
                {beYearOptions().map(y => <option key={y} value={y}>พ.ศ. {y}</option>)}
              </Form.Select>
              <Form.Select size="sm" style={{ width: 220 }} value={type} onChange={e => setType(e.target.value)}>
                <option value="">ทุกประเภท</option>
                {Object.entries(CERT_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </Form.Select>
              <Form.Select size="sm" style={{ width: 150 }} value={status} onChange={e => setStatus(e.target.value)}>
                <option value="">ทุกสถานะ</option>
                {Object.entries(CERT_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </Form.Select>
              <Form.Control size="sm" style={{ maxWidth: 200 }} placeholder="ค้นหา เลขที่/ชื่อ"
                value={q} onChange={e => setQ(e.target.value)} />
              <div className="ms-auto text-muted fs-sm">{certs.length} รายการ</div>
            </Card.Header>
            <Card.Body className="p-0">
              {loading ? (
                <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
              ) : certs.length === 0 ? (
                <div className="text-center text-muted py-4">ไม่พบหนังสือรับรองในเงื่อนไขที่เลือก</div>
              ) : (
                <Table responsive hover className="mb-0 fs-sm">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 90 }}>เลขที่</th>
                      <th style={{ width: 200 }}>ประเภท</th>
                      <th>ผู้ขอ</th>
                      <th>นำไปใช้เพื่อ</th>
                      <th style={{ width: 120 }}>วันที่ออก</th>
                      <th style={{ width: 130 }}>สถานะ</th>
                      <th style={{ width: 170 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {certs.map(c => {
                      const tt = CERT_TYPES[c.ct_type]
                      const st = CERT_STATUS[c.ct_status] || CERT_STATUS.requested
                      const [, nextLabel] = nextStatus[c.ct_status] || []
                      return (
                        <tr key={c.ct_id}>
                          <td className="fw-semibold">{c.ct_full_no}</td>
                          <td><Badge bg={tt?.bg || 'secondary'}>{tt?.label || c.ct_type}</Badge></td>
                          <td>
                            {c.st_name ? `${c.st_name} (${c.st_idnumber})` : c.t_name || '-'}
                          </td>
                          <td className="text-truncate" style={{ maxWidth: 180 }} title={c.ct_purpose || ''}>{c.ct_purpose || '-'}</td>
                          <td>{fmtDateTH(c.ct_issue_date)}</td>
                          <td><Badge bg={st.bg}>{st.label}</Badge></td>
                          <td className="text-end">
                            <Button as={Link} to={`/admin-office/certificates/${c.ct_id}/print`}
                              size="sm" variant="outline-secondary" className="me-1" title="พิมพ์">
                              <i className="ri-printer-line" />
                            </Button>
                            {nextLabel && (
                              <Button size="sm" variant="primary" onClick={() => handleStatus(c)}>{nextLabel}</Button>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Container>
      </div>
      <Footer />

      {/* Create modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">รับคำขอหนังสือรับรอง</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {modalError && <Alert variant="danger" className="py-2">{modalError}</Alert>}
            <Form.Group className="mb-2">
              <Form.Label className="fs-sm mb-1">ประเภท</Form.Label>
              <Form.Select size="sm" value={form.ct_type}
                onChange={e => setForm(f => ({ ...f, ct_type: e.target.value }))}>
                {Object.entries(CERT_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </Form.Select>
            </Form.Group>

            {isStudentCert ? (
              <Form.Group className="mb-2">
                <Form.Label className="fs-sm mb-1">นักเรียน <span className="text-danger">*</span></Form.Label>
                {selectedStudent ? (
                  <div className="border rounded p-2 d-flex align-items-center justify-content-between">
                    <span>{selectedStudent.st_name} ({selectedStudent.st_idnumber})
                      {selectedStudent.c_class ? ` · ${selectedStudent.c_class}/${selectedStudent.r_no}` : ''}</span>
                    <Button size="sm" variant="outline-secondary" onClick={() => setSelectedStudent(null)}>เปลี่ยน</Button>
                  </div>
                ) : (
                  <>
                    <Form.Control size="sm" placeholder="พิมพ์ชื่อหรือเลขประจำตัวนักเรียน (อย่างน้อย 2 ตัวอักษร)"
                      value={studentQuery} onChange={e => setStudentQuery(e.target.value)} />
                    {studentResults.length > 0 && (
                      <ListGroup className="mt-1" style={{ maxHeight: 180, overflowY: 'auto' }}>
                        {studentResults.map(s => (
                          <ListGroup.Item key={s.st_id} action className="py-1 fs-sm"
                            onClick={() => { setSelectedStudent(s); setStudentResults([]) }}>
                            {s.st_name} ({s.st_idnumber}){s.c_class ? ` · ${s.c_class}/${s.r_no}` : ''}
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    )}
                  </>
                )}
              </Form.Group>
            ) : (
              <Form.Group className="mb-2">
                <Form.Label className="fs-sm mb-1">บุคลากร <span className="text-danger">*</span></Form.Label>
                <Form.Select size="sm" value={selectedTeacher} onChange={e => setSelectedTeacher(e.target.value)}>
                  <option value="">— เลือกบุคลากร —</option>
                  {teachers.map(t => <option key={t.t_id} value={t.t_id}>{t.t_name}</option>)}
                </Form.Select>
              </Form.Group>
            )}

            <Row className="g-2">
              <Col xs={7}>
                <Form.Label className="fs-sm mb-1">นำไปใช้เพื่อ</Form.Label>
                <Form.Control size="sm" type="text" maxLength={500} value={form.ct_purpose}
                  placeholder="เช่น ประกอบการสมัครเรียนต่อ / ทำธุรกรรม"
                  onChange={e => setForm(f => ({ ...f, ct_purpose: e.target.value }))} />
              </Col>
              <Col xs={5}>
                <Form.Label className="fs-sm mb-1">หมายเหตุ</Form.Label>
                <Form.Control size="sm" type="text" maxLength={500} value={form.ct_note}
                  onChange={e => setForm(f => ({ ...f, ct_note: e.target.value }))} />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" size="sm" onClick={() => setShowModal(false)}>ยกเลิก</Button>
            <Button variant="primary" size="sm" type="submit" disabled={saving}>
              {saving ? <Spinner size="sm" className="me-1" /> : <i className="ri-award-line me-1" />}
              รับคำขอ
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  )
}
